import { SkillCategory } from '@applicator/shared'
import { RefObject, useEffect, useState } from 'react'

/** Offset from top of scroll root to probe line (below sticky Skills section header). */
const SKILLS_STICKY_PROBE_PX = 52

export function useActiveSkillCategoryScroll(
  categories: SkillCategory[],
  scrollRootRef: RefObject<HTMLElement | null> | undefined,
  containerRef: RefObject<HTMLElement | null>
) {
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null)

  useEffect(() => {
    const root = scrollRootRef?.current
    const container = containerRef.current
    if (!root || !container || categories.length === 0) {
      setActiveCategoryId(null)
      return
    }

    const resolveActive = () => {
      const blocks = container.querySelectorAll<HTMLElement>('[data-skill-category-id]')
      if (!blocks.length) {
        setActiveCategoryId(null)
        return
      }

      const rootTop = root.getBoundingClientRect().top
      const probeY = rootTop + SKILLS_STICKY_PROBE_PX

      let activeId: string | null = null
      for (const block of Array.from(blocks)) {
        const { top } = block.getBoundingClientRect()
        if (top <= probeY) {
          activeId = block.getAttribute('data-skill-category-id')
        } else {
          break
        }
      }
      setActiveCategoryId(activeId)
    }

    resolveActive()
    root.addEventListener('scroll', resolveActive, { passive: true })
    window.addEventListener('resize', resolveActive)
    return () => {
      root.removeEventListener('scroll', resolveActive)
      window.removeEventListener('resize', resolveActive)
    }
  }, [categories, scrollRootRef, containerRef])

  const activeCategory = activeCategoryId
    ? categories.find((c) => c.id === activeCategoryId)
    : undefined
  const activeCategoryName = activeCategory
    ? activeCategory.name?.trim() || 'Unnamed Category'
    : null

  return { activeCategoryId, activeCategoryName }
}
